import messages from "messages";
import { useCallback, useEffect, useState, useRef } from "react";
import { Button, Form, Alert } from "react-bootstrap";
// components
import ConfirmModal from "components/confirm-modal";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import PatientEditor from "./PatientEditor";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";
// constants
import { PATIENT_TYPES } from "./constants/patientTypes";
// types
import { AmplitudePageName, track } from "utils/amplitude";
import type { PatientForPatientEditor } from "./types";
import { useOrchidPro } from "components/Dashboard/DoctorDashboard/contexts/orchid-pro-context";
// services
import createNewCouplePatient from "services/patient-chart/createNewCouplePatient";
import createNewMinorPatient from "services/patient-chart/createNewMinorPatient";
import createNewPatient from "services/patient-chart/createNewPatient";
import putPatientProfile from "services/patient-chart/putPatientProfile";

// styles
import styles from "./PatientEditor.module.scss";
import { useClientsContext } from "contexts/ClientsContext";

const ANALYTICS_COMPONENT_NAME = "patient-editor-modal";

const PatientCreatorContainer = ({
  show,
  onHide,
  nextAction,
  defaultType,
  coupleOrContactUser,
  amplitudePageName,
  addCoupleOrContactAsPatient,
}: {
  show: boolean;
  onHide: () => void;
  addCoupleOrContactAsPatient?: boolean;
  nextAction?: (arg?: unknown) => void;
  defaultType?: string;
  coupleOrContactUser?: PatientChartContact;
  amplitudePageName: AmplitudePageName;
}) => {
  const { practiceOwnerOrchidUserId } = useTeamContext();
  const { refreshClients } = useClientsContext();

  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<any>();
  const [validated, setValidated] = useState<boolean>(false);
  const [type, setType] = useState<string>(PATIENT_TYPES["adult"].value);

  const [values, setValues] = useState<PatientForPatientEditor[]>([{}]);

  const [billingType, setBillingType] = useState<string>("self-pay");
  const [billingContact, setBillingContact] = useState<string>("patient1");
  const [guardianType, setGuardianType] = useState<string>("parent");

  const [showConfirmEmail, setShowConfirmEmail] = useState<boolean>(false);
  const [displayEmail, setDisplayEmail] = useState<string>("");

  const [waitlist, setWaitlist] = useState<boolean>(false);
  const [waitlistDateAdded, setWaitlistDateAdded] = useState<string>(
    new Date().toISOString(),
  );
  const [reasonForVisit, setReasonForVisit] = useState<string>("");

  const [requestedTimes, setRequestedTimes] = useState<
    { date: string; time: string }[]
  >([{ date: new Date().toISOString(), time: "09:00" }]);

  const { orchidPro } = useOrchidPro();

  const handleCreatePatient = async () => {
    const { patient } = await createNewPatient(
      {
        ...values[0],
        email: values[0].email?.trim(),
        billing_type: billingType,
        orchid_pro_requested_welcome_email_to_send:
          orchidPro?.send_welcome_email_to_new_patients,
        ...(waitlist && {
          client_status: "waitlist",
          waitlist_date_added: waitlistDateAdded,
          reason_for_visit: reasonForVisit,
          requested_times: requestedTimes,
        }),
      },
      practiceOwnerOrchidUserId,
    );

    refreshClients();

    return {
      couple_members: null,
      guardians: null,
      patient: {
        ...patient,
        orchid_user_id: patient.patient_user_id,
      },
    };
  };

  const handleCreateCouple = async () => {
    const { patient1, patient2, couple_entry } = await createNewCouplePatient({
      patient1: {
        ...values[0],
        email: values[0].email?.trim(),
        orchid_pro_requested_welcome_email_to_send:
          orchidPro?.send_welcome_email_to_new_patients,
      },
      patient2: {
        ...values[1],
        email: values[1].email?.trim(),
        orchid_pro_requested_welcome_email_to_send:
          orchidPro?.send_welcome_email_to_new_patients,
      },
      billingUser: billingContact === "patient1" ? "partner1" : "partner2",
      patient1UserId: values[0]?.cognito_sub,
      patient2UserId: values[1]?.cognito_sub,
      billingType,
      ...(waitlist && {
        client_status: "waitlist",
        waitlistDateAdded,
        reasonForVisit,
        requestedTimes,
      }),
    });

    refreshClients();

    return {
      couple_members: [
        { ...patient1, orchid_user_id: patient1.patient_user_id },
        { ...patient2, orchid_user_id: patient2.patient_user_id },
      ],
      guardians: null,
      patient: {
        ...couple_entry,
        orchid_user_id: couple_entry.patient_user_id,
      },
    };
  };

  const handleCreateMinor = async () => {
    const { minor_patient, guardian } = await createNewMinorPatient({
      minorPatient: {
        ...values[0],
        email: values[0].email?.trim(),
        orchid_pro_requested_welcome_email_to_send:
          orchidPro?.send_welcome_email_to_new_patients,
        billing_type: billingType,
        ...(waitlist && {
          client_status: "waitlist",
          waitlist_date_added: waitlistDateAdded,
          reason_for_visit: reasonForVisit,
          requested_times: requestedTimes,
        }),
      },
      guardian: {
        ...values[1],
        email: values[1].email?.trim(),
        orchid_pro_requested_welcome_email_to_send:
          orchidPro?.send_welcome_email_to_new_patients,
      },
      guardianIsBillingUser: billingContact === "patient2" ? true : false,
      guardianRelationship: guardianType,
      minorPatientUserId: values[0]?.cognito_sub,
      guardianUserId: values[1]?.cognito_sub,
    });

    refreshClients();

    return {
      couple_members: null,
      guardians: [
        {
          contact: {
            ...guardian,
            orchid_user_id: guardian.patient_user_id,
          },
        },
      ],
      patient: {
        ...minor_patient,
        orchid_user_id: minor_patient.patient_user_id,
      },
    };
  };

  const handleApply = async () => {
    setBusy(true);
    setError(undefined);
    let nextVal = {} as any;

    try {
      if (type === "adult") {
        nextVal = await handleCreatePatient();
      } else if (type === "couple") {
        nextVal = await handleCreateCouple();
      } else if (type === "minor") {
        nextVal = await handleCreateMinor();
      }
      onHide();

      setType("adult");
      setValues([{}]);
      setBillingType("self-pay");
      setBillingContact("patient1");
      setGuardianType("parent");
      setWaitlist(false);
      setWaitlistDateAdded(new Date().toISOString());
      setReasonForVisit("");
      setRequestedTimes([{ date: new Date().toISOString(), time: "09:00" }]);

      if (nextAction) {
        nextAction(nextVal);
      }
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  };

  const submitForm = (event) => {
    event.preventDefault();

    // form check
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      setValidated(true);
      setError("Please fill in all required fields.");
      return;
    }

    // validate email
    if (values.some(({ email }) => !!email)) {
      const emailsForDisplay = [...values.map(({ email }) => email)].join(" ");
      setDisplayEmail(emailsForDisplay);
      setShowConfirmEmail(true);
    } else {
      // api requests
      handleApply();
    }
  };

  const handleAddTime = () => {
    setRequestedTimes([
      ...requestedTimes,
      {
        date: new Date().toISOString(),
        time: "09:00",
      },
    ]);
  };

  const handleTimeChange = (index, field, value) => {
    const updatedTimes = [...requestedTimes];

    if (field === "date") {
      updatedTimes[index][field] = new Date(value).toISOString();
    } else {
      updatedTimes[index][field] = value;
    }

    setRequestedTimes(updatedTimes);
  };

  useEffect(() => {
    if (type === "minor") {
      setBillingContact("patient2");
    } else {
      setBillingContact("patient1");
    }
    if (
      (type === "couple" && coupleOrContactUser) ||
      (addCoupleOrContactAsPatient && coupleOrContactUser)
    ) {
      setValues([coupleOrContactUser]);
    }
  }, [type, coupleOrContactUser, addCoupleOrContactAsPatient]);

  useEffect(() => {
    if (defaultType) {
      setType(defaultType);
    }
  }, [defaultType]);

  const changeCoupleOrContactToPatient = useCallback(
    async (e) => {
      e.preventDefault();

      if (!addCoupleOrContactAsPatient || !coupleOrContactUser || !values)
        return;

      try {
        const overrideFields = {};
        for (const key in values[0]) {
          overrideFields[`${key}_override`] = values[0][key];
        }

        await putPatientProfile(coupleOrContactUser.orchid_user_id, {
          client_status: "patient",
          ...overrideFields,
        });

        onHide();
        window.location.reload();
      } catch (error) {
        console.error(error);
      }
    },
    [addCoupleOrContactAsPatient, coupleOrContactUser, onHide, values],
  );

  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [error]);

  return (
    <>
      <ModalLayout
        size="lg"
        showHeaderDivider
        show={show}
        onHide={onHide}
        Header={messages.createANewPatient}
        headerClass={styles["patient-editor__header"]}
        bodyClass={styles["patient-editor__body"]}
        footerClass={styles["patient-editor__footer"]}
        dialogClassName={styles["patient-editor__dialog"]}
      >
        <Form
          noValidate
          validated={validated}
          onSubmit={(e) => {
            if (addCoupleOrContactAsPatient && coupleOrContactUser) {
              track(
                amplitudePageName,
                [ANALYTICS_COMPONENT_NAME, "add-couple-as-client-button"],
                "click",
              );

              changeCoupleOrContactToPatient(e);
            } else {
              track(
                amplitudePageName,
                [ANALYTICS_COMPONENT_NAME, "create-button"],
                "click",
              );

              submitForm(e);
            }
          }}
          className={styles["patient-editor__form"]}
        >
          {error && (
            <Alert
              variant={"danger"}
              className={styles["patient-editor__error"]}
              ref={errorRef}
            >
              {error?.toString()}
            </Alert>
          )}
          <PatientEditor
            type={type}
            setType={setType}
            values={values}
            setValues={setValues}
            billingType={billingType}
            setBillingType={setBillingType}
            billingContact={billingContact}
            setBillingContact={setBillingContact}
            setGuardianType={setGuardianType}
            coupleOrContactUser={coupleOrContactUser}
            addCoupleOrContactAsPatient={addCoupleOrContactAsPatient}
            amplitudePageName={amplitudePageName}
            waitlist={waitlist}
            setWaitlist={setWaitlist}
            waitlistDateAdded={waitlistDateAdded}
            setWaitlistDateAdded={setWaitlistDateAdded}
            reasonForVisit={reasonForVisit}
            setReasonForVisit={setReasonForVisit}
            requestedTimes={requestedTimes}
            setRequestedTimes={setRequestedTimes}
            handleTimeChange={handleTimeChange}
            handleAddTime={handleAddTime}
          />
          <div className={styles["patient-editor__footer"]}>
            <Button
              disabled={busy}
              variant={"outline-primary"}
              onClick={() => {
                track(
                  amplitudePageName,
                  [ANALYTICS_COMPONENT_NAME, "cancel-button"],
                  "click",
                );
                onHide();
              }}
            >
              {messages.cancel}
            </Button>
            <Button disabled={busy} type="submit">
              {messages.create}
            </Button>
          </div>
        </Form>
      </ModalLayout>
      {showConfirmEmail && (
        <ConfirmModal
          show={showConfirmEmail}
          onConfirm={() => {
            setShowConfirmEmail(false);
            handleApply();
          }}
          onCancel={() => setShowConfirmEmail(false)}
          labelTitle={"Are you sure?"}
          labelCancel={messages.noKeepEditing}
          labelConfirm={"Yes, I confirm"}
        >
          <div>
            Are you sure <b>{displayEmail}</b> is the correct email address?
            <br />
            <br />
            Please confirm this is the correct email address to ensure you do
            not disclose protected health information (PHI) inadvertently. Your
            confirmation will be recorded
          </div>
        </ConfirmModal>
      )}
    </>
  );
};

export default PatientCreatorContainer;
